import { useEffect } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { useI18n } from '@/i18n'
import type { DatasetMeta, Language } from '@/types'

const links = [
    { to: '/', key: 'nav.overview', end: true },
    { to: '/resources', key: 'nav.resources', end: false },
    { to: '/profiles', key: 'nav.profiles', end: false },
    { to: '/matrix', key: 'nav.matrix', end: false },
    { to: '/glossary', key: 'nav.glossary', end: false },
    { to: '/about', key: 'nav.about', end: false },
]

function LanguageSwitch() {
    const { language, setLanguage } = useI18n()
    const options: Language[] = ['en', 'cs']
    return (
        <div className="langswitch" role="group" aria-label="Language">
            {options.map((option, index) => (
                <span key={option}>
                    {index > 0 ? <span className="langswitch__sep">| </span> : null}
                    <button
                        type="button"
                        aria-pressed={language === option}
                        onClick={() => setLanguage(option)}
                    >
                        {option === 'en' ? 'English' : 'Čeština'}
                    </button>
                </span>
            ))}
        </div>
    )
}

export function Layout({ meta }: { meta: DatasetMeta | null }) {
    const { t, language, localized, formatDate } = useI18n()
    const { pathname } = useLocation()

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [pathname])

    return (
        <div className="layout">
            <div className="notice" role="note" aria-label={t('common.demoData')}>
                <div className="shell notice__inner">
                    <span className="notice__badge">{t('common.demoBadge')}</span>
                    <span>{t('common.demoNotice')}</span>
                    <Link className="notice__link" to="/about">
                        {t('about.dataTitle')}
                    </Link>
                </div>
            </div>

            <header className="topbar">
                <div className="shell topbar__inner">
                    <Link className="brand" to="/">
                        <img className="brand__logo" src="/brand/eosc-cz-logo.svg" alt="EOSC CZ" />
                        <span className="brand__divider" />
                        <span>
                            <span className="brand__name">{t('app.title')}</span>
                            <span className="brand__tag">{t('app.owner')}</span>
                        </span>
                    </Link>
                    <LanguageSwitch />
                </div>
            </header>

            <nav className="navbar">
                <div className="shell navbar__inner">
                    {links.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.end}
                            className={({ isActive }) =>
                                `nav__link${isActive ? ' nav__link--active' : ''}`
                            }
                        >
                            {t(link.key)}
                        </NavLink>
                    ))}
                </div>
            </nav>

            <main className="content">
                <Outlet />
            </main>

            <footer className="footer">
                <div className="shell footer__inner">
                    <img
                        className="footer__acks"
                        src={`/brand/eu-msmt.${language}.svg`}
                        alt={t('footer.funding')}
                    />
                    <div className="footer__meta">
                        <span>{t('app.subtitle')}</span>
                        {meta ? (
                            <span>
                                {localized(meta.name)} · v{meta.version} ·{' '}
                                {formatDate(meta.updated)}
                            </span>
                        ) : null}
                        <a href="https://www.eosc.cz/" target="_blank" rel="noreferrer noopener">
                            eosc.cz
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    )
}
